import { UrlExpiredMessage, UrlExpiredMessageTypes } from 'containers/UrlExpired/UrlExpired';
import { ADDRESS, buildResourceLink } from 'lib/communication';
import { generateTempLink } from 'lib/communication/files';
import { useCallback, useEffect } from 'react';
import { GA_newsLinkClicked } from 'lib/gtm-utils';

enum MouseButton {
    Left = 0,
    Middle = 1,
    Right = 2,
}

const trackNewsLinkClick = (anchor: HTMLAnchorElement) => {
    const article = anchor.closest('[data-news-id]');
    if (article === null) return;

    GA_newsLinkClicked({
        newsId: article.getAttribute('data-news-id'),
        newsTitle: article.getAttribute('data-news-title'),
        url: anchor.getAttribute('href') ?? '',
        isImage: anchor.querySelector('img') !== null
    });
};

export default function LinkInterceptor() {
    const redirect = async (filename: string, target: string | null) => {
        const url = await generateTempLink(filename);
        if (url === '') return;
        const child = window.open(buildResourceLink(url), target ?? '_blank');
        if (child && target !== '_self') child.sessionStorage.clear();
    };

    const urlExpiredMessageHandler = useCallback(async (event: MessageEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.origin !== window.origin) {
            return;
        }

        const { type, content } = event.data as UrlExpiredMessage;
        let url = '';
        switch (type) {
            case UrlExpiredMessageTypes.GeneratePreviewLink:
                url = await generateTempLink(content, true);
                (event.source as Window).postMessage({ type: UrlExpiredMessageTypes.OpenNewPreview, content: url }, event.origin);
                break;
            default:
                break;
        }
    }, []);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (event.button === MouseButton.Right) return;
            const target = event.target as HTMLElement;
            const anchor = target.closest('a');

            if (anchor) {
                trackNewsLinkClick(anchor);

                const href = anchor.getAttribute('href');
                if (href === null ||
                    href.includes('blob:') ||
                    ((href.includes('http://') || href.includes('https://')) && !href.includes(`${ADDRESS}`)) ||
                    !href.includes('storage/')) return;

                const parts = href.split('storage/');
                const filename = parts[parts.length - 1];
                event.preventDefault();
                const targetPage = event.button === MouseButton.Middle ? '_blank' : anchor.getAttribute('target');
                redirect(filename, targetPage);
            }
        };

        document.addEventListener('click', handleClick);
        document.addEventListener('auxclick', handleClick);
        window.addEventListener('message', urlExpiredMessageHandler, false);
        return () => {
            document.removeEventListener('click', handleClick);
            document.removeEventListener('auxclick', handleClick);
            window.removeEventListener('message', urlExpiredMessageHandler, false);
        };
    }, []);

    return null;
}
